import {DateTime} from 'luxon';
import type {Writable} from 'svelte/store';
import {writable} from 'svelte/store';
import type {IncentivePlanDeal} from 'Types/incentivePlan/incentiveTypes';
import {
  getIncentivePlanDeal,
  getInvestorPriceDetailsJwt,
} from 'Utility/incentivePlan/getIncentive';

const ZONE = 'America/New_York';
const initIncentivePlan = () => {
  const {set, subscribe, update} = writable<IncentivePlanDeal>();

  const timeDiff = (endAt: string): number => {
    const timeNow = DateTime.now().setZone(ZONE);
    const endAtTime = DateTime.fromISO(endAt).setZone(ZONE);
    return endAtTime.diff(timeNow).milliseconds;
  };

  const load = async (id: string | number): Promise<void> => {
    let incentive = await getIncentivePlanDeal(id);
    const endAt = incentive?.effective_tier?.end_at;
    set(incentive);

    if (!endAt) return;

    setTimeout(async () => {
      incentive = await getIncentivePlanDeal(id);
      set(incentive);
    }, timeDiff(endAt) + 1000);
  };

  const loadInvestor = async (
    id: string | number,
    jwt: string
  ): Promise<void> => {
    const {full_price, final_price, effective_tier, incentive_plan} =
      await getInvestorPriceDetailsJwt(id, jwt);

    set({
      full_price,
      final_price,
      effective_tier,
      incentive_plan,
    });
  };

  return {
    set,
    subscribe,
    update,
    load,
    loadInvestor,
  };
};

/**
 * Represents a store for managing incentive plans.
 */
export interface IncentivePlanStore extends Writable<IncentivePlanDeal> {
  /**
   * Loads an incentive plan by its ID.
   * @param id - The ID of the incentive plan. It can be either a uid or the id.
   * @returns A promise that resolves when the incentive plan is loaded.
   */
  load: (id: string | number) => Promise<void>;
  /**
   * Loads an incentive plan by deal and investor ids. It's purpose is to be used
   * in embedded contexts before the investor has set an investment amount and
   * the user has not authenticated.
   * @param id - The ID of the incentive plan. It can be either a uid or the id.
   * @param investorId - The ID of the investor
   * @param jwt - a valid JWT token for the investor
   * @returns A promise that resolves when the incentive plan is loaded.
   */
  loadInvestor: (id: string | number, jwt: string) => Promise<void>;
}

export const incentivePlans: IncentivePlanStore = initIncentivePlan();
